import React, { useState, useEffect, useRef } from 'react';
import { getAuthToken } from './authToken';
import PpiBadge, { AllegroSystemMessage } from './PpiBadge';
import { useShortcuts } from './useShortcuts';
import OfferPicker from './OfferPicker';
import ShortcutSuggestions from './ShortcutSuggestions';
import LiveAlertBar from './LiveAlertBar';
import AIAssistant from './AIAssistant';
import axios from 'axios';
import Ratings from './Ratings';
import Issues from './Issues';
import Settings from './Settings';
import EmailInbox from './EmailInbox';
import AssignButton from './AssignButton';
import BuyerReturnsButton from './BuyerReturnsButton';
import BuyerShipmentsButton from './BuyerShipmentsButton';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import DictateButton from './DictateButton';

const API = '/api';

interface Thread {
  id: string;
  read: boolean;
  lastMessageDateTime: string;
  interlocutor: { login: string; avatarUrl: string; };
  account_id?: string;
  account_name?: string;
  has_attachments?: boolean;
  language?: string;
  auto_reply_disabled?: boolean;
  assigned_to?: string | null;
}

interface ThreadMessage {
  id: string;
  text: string;
  author: { login: string; };
  createdAt: string;
  attachments?: any[];
}

const LANG_FLAGS: {[key:string]:string} = {
  'en':'🇬🇧','de':'🇩🇪','cs':'🇨🇿','sk':'🇸🇰','ro':'🇷🇴','hu':'🇭🇺',
  'uk':'🇺🇦','ru':'🇷🇺','fr':'🇫🇷','it':'🇮🇹','es':'🇪🇸','nl':'🇳🇱',
  'bg':'🇧🇬','el':'🇬🇷','sv':'🇸🇪','lt':'🇱🇹','lv':'🇱🇻','et':'🇪🇪',
};

function decodeHtml(html: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function formatDate(d: string): string {
  if (!d) return '';
  const s = d.replace(' ', 'T');
  const withZ = !s.includes('Z') && !s.includes('+') ? s + 'Z' : s;
  const date = new Date(withZ);
  const diff = new Date().getTime() - date.getTime();
  if (diff < 86400000) return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
}

const SECTION_LABELS: {[key:string]:string} = {
  list: 'Wiadomości',
  thread: 'Wiadomości',
  ratings: 'Oceny',
  issues: 'Dyskusje i reklamacje',
  settings: 'Ustawienia',
  email: 'Email',
};

export default function AppMobile({ currentUser, onLogout, onSettings, onHome, syncWarning = false, initialView = 'list', onOpenAiOpen, onOpenReturns, onOpenReturn, onOpenShipment }: {
  currentUser: any;
  onLogout: () => void;
  onSettings: () => void;
  initialView?: string;
  onHome?: () => void;
  syncWarning?: boolean;
  onOpenAiOpen?: () => void;
  onOpenReturns?: () => void;
  onOpenReturn?: (ref: string) => void;
  onOpenShipment?: (orderId: string, accountId: string) => void;
}) {
  const hasSettings = (currentUser?.modules || []).includes('settings');
  const [threads, setThreads] = useState<Thread[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [selected, setSelected] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [reply, setReply] = useState('');
  const { showSuggestions: showReplySuggestions, suggestions: replySuggestions, handleChange: handleReplyChange, applyShortcut: applyReplyShortcut, setShowSuggestions: setShowReplySuggestions } = useShortcuts();
  const [loading, setLoading] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentLearned, setSentLearned] = useState(false);
  const [filter, setFilter] = useState('unread');
  const [aiLoading, setAiLoading] = useState(false);
  // ostatnia propozycja AI w tej rozmowie (do 'Wyslij i ucz': regula powstaje tylko z poprawek operatora / jego wskazowki)
  const lastAiRef = useRef<{ id: string; draft: string; hint: string } | null>(null);
  const [threadLang, setThreadLang] = useState('pl');
  const [showMsgHistory, setShowMsgHistory] = useState(false);
  const [showIssueHistoryMobile, setShowIssueHistoryMobile] = useState(false);
  const [issueHistoryMobile, setIssueHistoryMobile] = useState<any[]>([]);
  const [issueHistoryMobileLoading, setIssueHistoryMobileLoading] = useState(false);
  const [msgHistory, setMsgHistory] = useState<any[]>([]);
  const [msgHistoryLoading, setMsgHistoryLoading] = useState(false);
  const [translations, setTranslations] = useState<{[key:string]:string}>({});
  const [showTranslation, setShowTranslation] = useState<{[key:string]:boolean}>({});
  const [replyTranslation, setReplyTranslation] = useState('');
  const [attachments, setAttachments] = useState<{id: string, filename: string}[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [replyIsManual, setReplyIsManual] = useState(false);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [showOfferPicker, setShowOfferPicker] = useState(false);
  const [offerInfo, setOfferInfo] = useState<any>(null);
  const [showOrders, setShowOrders] = useState(false);
  const [buyerOrders, setBuyerOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [translatingReply, setTranslatingReply] = useState(false);
  const [classification, setClassification] = useState<any>(null);
  const [view, setView] = useState<'list'|'thread'|'ratings'|'issues'|'settings'|'email'|'no-access'>(initialView as any);
  const [emailKey, setEmailKey] = useState(0);
  const [settingsKey, setSettingsKey] = useState(0);
  const [selectedMobileIds, setSelectedMobileIds] = useState<Set<string>>(new Set());
  const [negativeRatings, setNegativeRatings] = useState(0);
  const [expiredAccounts, setExpiredAccounts] = useState<string[]>([]);
  const isAccountExpired = (accountName: string) => expiredAccounts.includes(accountName);
  const [search, setSearch] = useState('');
  const [mobilePage, setMobilePage] = useState(0);
  const [totalThreadsMobile, setTotalThreadsMobile] = useState(0);
  const [copyToast, setCopyToast] = useState('');
  const MOBILE_PAGE = 50;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [issuesKey, setIssuesKey] = useState(0);
  const [ratingsKey, setRatingsKey] = useState(0);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);

  useEffect(() => {
    fetchThreads();
    axios.get(`${API}/allegro/accounts`).then(r => {
      setAccounts(r.data || []); setAccountsLoaded(true);
      const now = new Date();
      const expired = (r.data || []).filter((a: any) => a.expires_at && new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now).map((a: any) => a.name);
      setExpiredAccounts(expired);
    }).catch(() => { setAccountsLoaded(true); });
    axios.get(`${API}/allegro/ratings?limit=50`).then(r => {
      const neg = (r.data.ratings || []).filter((x: any) => !x.recommended && !x.answer?.message);
      setNegativeRatings(neg.length);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Otwieranie z parametrow URL (ramka w module Przesylki: /?open=messages|issues&login=X) — mobile
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const open = p.get('open');
    const login = p.get('login') || '';
    if (!open) return;
    if (open === 'messages') {
      if (login) setSearch(login);
      setFilter('all');
      setView('list');
      setTimeout(() => { try { fetchThreads(0, 'all', login); } catch (e) {} }, 400);
    } else if (open === 'issues') {
      if (login) localStorage.setItem('issues_prefill_search', login);
      localStorage.setItem('issues_prefill_filter', 'all');
      setView('issues');
      setIssuesKey(k => k + 1);
      setSelectedIssue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchThreads = async (page: number = 0, f?: string, s?: string) => {
    setLoading(true);
    try {
      const currentFilter = f !== undefined ? f : filter;
      const currentSearch = s !== undefined ? s : search;
      const params = new URLSearchParams({
        offset: String(page * MOBILE_PAGE),
        limit: String(MOBILE_PAGE),
      });
      if (currentFilter === 'unread') params.set('filter_unread', 'true');
      if (currentSearch.trim()) params.set('search', currentSearch.trim());
      const res = await axios.get(`${API}/allegro/messages?${params.toString()}`);
      setThreads(res.data.threads || []);
      setTotalThreadsMobile(res.data.total || 0);
      setMobilePage(page);
    } catch(e) {}
    setLoading(false);
  };

  const selectThread = async (t: Thread) => {
    setSelected(t);
    setView('thread');
    setMessages([]);
    setLoadingMsgs(true);
    try {
      const res = await axios.get(`${API}/allegro/messages/${t.id}?account_id=${t.account_id||''}`);
      const msgs = (res.data.messages || []).sort((a: ThreadMessage, b: ThreadMessage) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setMessages(msgs);
      setOfferInfo(res.data.offer_info || null);
      // Scrolluj na dol tylko gdy watek jest nieprzeczytany
      if (!t.read) {
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
      // Ustaw jezyk od razu z bazy (t.language) - nie czekaj na API
      if (t.language && t.language !== 'pl') {
        setThreadLang(t.language);
      }
      const clientMsgs = msgs.filter((m: ThreadMessage) => m.author?.login === t.interlocutor.login);
      if (clientMsgs.length > 0) {
        const lastMsg = clientMsgs[clientMsgs.length - 1].text || '';
        const decoded = lastMsg.replace(/&[a-z]+;/gi, ' ');
        try {
          const [langRes, classRes] = await Promise.all([
            axios.post(`${API}/ai/detect-language`, { text: decoded }),
            axios.post(`${API}/ai/classify`, { message: decoded })
          ]);
          const detectedLang = langRes.data.language || 'pl';
          setThreadLang(detectedLang);
          setClassification(classRes.data);
        } catch(e) {}
      }
      setTranslations({});
      setReplyTranslation('');
    } catch(e) {}
    setLoadingMsgs(false);
  };

  const fetchBuyerOrders = async () => {
    if (!selected) return;
    setLoadingOrders(true);
    setShowOrders(true);
    try {
      const res = await axios.get(`${API}/allegro/buyer-orders`, {
        params: { buyer_login: selected.interlocutor.login, account_id: selected.account_id || '' }
      });
      setBuyerOrders(res.data.orders || []);
    } catch (e) { console.error(e); }
    setLoadingOrders(false);
  };

  const fetchIssueOrders = async () => {
    if (!selectedIssue) return;
    setLoadingOrders(true);
    setShowOrders(true);
    try {
      const res = await axios.get(`${API}/allegro/buyer-orders`, {
        params: { buyer_login: selectedIssue.buyer?.login || '', account_id: selectedIssue.account_id || '' }
      });
      setBuyerOrders(res.data.orders || []);
    } catch (e) { console.error(e); }
    setLoadingOrders(false);
  };

  const uploadFile = async (file: File) => {
    if (!selected) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('account_id', selected.account_id || '');
      formData.append('file', file);
      const res = await axios.post(`${API}/allegro/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachments(prev => [...prev, { id: res.data.attachment_id, filename: res.data.filename }]);
    } catch (e: any) {
      alert('Błąd uploadu: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setUploadingFile(false);
  };

  const sendReply = async (learn: boolean) => {
    if (!selected || !reply.trim()) return;
    // Sprawdz polaczenie przed wyslaniem
    try {
      const accountsRes = await axios.get(`${API}/allegro/accounts`);
      const now = new Date();
      const expired = (accountsRes.data || []).filter((a: any) =>
        a.id === selected.account_id && new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now
      );
      if (expired.length > 0) {
        alert(`Brak połączenia z kontem ${selected.account_name}. Odśwież połączenie w Ustawieniach.`);
        return;
      }
    } catch(e) {}
    try {
      let textToSend = reply;
      if (threadLang !== 'pl') {
        const transRes = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: threadLang });
        textToSend = transRes.data.translation;
      }
      await axios.post(`${API}/allegro/messages/${selected.id}/reply`, { text: textToSend, account_id: selected.account_id || '', attachment_ids: attachments.map(a => a.id), text_pl: threadLang !== 'pl' ? reply : '' });
      setAttachments([]);
        setAttachments([]);
      const clientMsgs = messages.filter(m => m.author?.login === selected.interlocutor.login);
      if (clientMsgs.length > 0) {
        const lastMsg = clientMsgs[clientMsgs.length-1].text || '';
        if (learn && replyIsManual) {
          await axios.post(`${API}/ai/approve-and-learn`, {
            question: lastMsg, answer: reply, category: classification?.category || 'ogolne',
            account_name: selected.account_name || '', ...(lastAiRef.current && lastAiRef.current.id === selected.id ? { ai_draft: lastAiRef.current.draft, ai_hint: lastAiRef.current.hint } : {})
          });
        }
      }
      await axios.post(`${API}/allegro/messages/${selected.id}/mark-read`);
      setSentLearned(!!(learn && replyIsManual && clientMsgs.length > 0));
      setSent(true);
      setReply('');
      setReplyTranslation('');
      setTimeout(() => setSent(false), 2000);
      selectThread(selected);
    } catch (e: any) {
      alert('Nie wysłano odpowiedzi: ' + ((e && e.response && e.response.data && e.response.data.detail) || (e && e.message) || 'nieznany błąd'));
    }
  };

  const generateAI = async () => {
    if (!selected || messages.length === 0) return;
    const clientMsgs = messages.filter(m => m.author?.login === selected.interlocutor.login);
    if (!clientMsgs.length) return;
    const lastMsg = decodeHtml(clientMsgs[clientMsgs.length-1].text || '');
    const history = messages.filter(m => !(m as any).allegro_system).map(m => ({
      text: decodeHtml(m.text||''),
      is_client: m.author?.login === selected.interlocutor.login
    }));
    setAiLoading(true);
    try {
      const offerContext = offerInfo ? `Produkt którego dotyczy wiadomość: ${offerInfo.name} (ID: ${offerInfo.id}, URL: ${offerInfo.url})` : '';
      // Generuj = szybka odpowiedz (bez raportu); odpowiedz na podstawie raportu sprawy daje przycisk Raport
      const res = await axios.post(`${API}/ai/generate-reply`, { message: lastMsg, history, account_id: selected.account_id || '', context: offerContext, hint: reply });
      lastAiRef.current = { id: selected.id, draft: res.data.draft || '', hint: reply };
      setReply(res.data.draft);
    } catch(e) {}
    setAiLoading(false);
  };

  const filtered = threads
    .filter(t => filter === 'unread' ? !t.read : true)
    .filter(t => !search.trim() || t.interlocutor.login.toLowerCase().includes(search.trim().replace(/\s+/g, " ").toLowerCase()))
    .sort((a,b) => new Date(b.lastMessageDateTime).getTime() - new Date(a.lastMessageDateTime).getTime())
    .slice(0, 100);

  // Stały górny pasek
  const TopBar = () => (
    <div style={{ background: '#1d4ed8', color: '#fff', padding: '10px 16px',
      display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onHome && <button onClick={onHome} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '0 4px', opacity: 0.85 }}>&#8592;</button>}
          <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 0.3 }}>OPENkontakt</div>
        </div>
        {expiredAccounts.length > 0 && (
          <div onClick={() => { if (hasSettings) { setView('settings'); } else { setView('no-access'); } }}
            style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#fef2f2',
              color: '#dc2626', border: '1px solid #fecaca', cursor: 'pointer', marginTop: 1 }}>
            ⚠️ {expiredAccounts.join(', ')}
          </div>
        )}
        {syncWarning && currentUser?.role === 'admin' && (
          <div style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#fef9c3',
            color: '#92400e', border: '1px solid #fde68a', marginTop: 1 }}>
            ⚠️ Sync nie działa
          </div>
        )}
        <div style={{ fontSize: 11, opacity: 0.8 }}>{SECTION_LABELS[view]}</div>
      </div>
      {view === 'list' && (
        <>
          <button onClick={() => fetchThreads(mobilePage)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>↻</button>
        </>
      )}
      {view === 'thread' && selected && (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={"Kliknij aby skopiować"} onClick={() => { navigator.clipboard.writeText(selected.interlocutor.login || ''); setCopyToast(selected.interlocutor.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'copy' }}>{selected.interlocutor.login}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>{selected.account_name}</div>
            <PpiBadge t={selected} />
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={async () => {
              setView('list'); setSelected(null); fetchThreads(mobilePage);
              await axios.post(`${API}/allegro/messages/${selected.id}/handle`);
            }} style={{ background: 'rgba(134,239,172,0.3)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>
              ✓
            </button>
            {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
              <button onClick={async () => {
                setShowMsgHistory(h => !h);
                if (!showMsgHistory) {
                  setMsgHistoryLoading(true);
                  const res = await axios.get(`${API}/allegro/messages/${selected.id}/history`);
                  setMsgHistory(res.data || []);
                  setMsgHistoryLoading(false);
                }
              }} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontSize: 11 }}>
                🕐
              </button>
            )}
            <button onClick={fetchBuyerOrders} style={{ background: 'rgba(147,197,253,0.3)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontSize: 11 }}>
              🛒
            </button>
            <BuyerReturnsButton buyerLogin={selected.interlocutor.login} variant='mobile' onOpenReturn={onOpenReturn || (() => {})} />
            <BuyerShipmentsButton buyerLogin={selected.interlocutor.login} variant='mobile' onOpenShipment={onOpenShipment || (() => {})} />
            <button onClick={async () => {
              const res = await axios.post(`${API}/allegro/messages/${selected.id}/toggle-auto-reply`);
              setSelected({...selected, auto_reply_disabled: res.data.auto_reply_disabled});
            }} style={{ background: selected.auto_reply_disabled ? 'rgba(252,165,165,0.3)' : 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontSize: 11 }}>
              {selected.auto_reply_disabled ? '🚫' : '🤖'}
            </button>
          </div>
        </>
      )}
      {view === 'issues' && selectedIssue && (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={"Kliknij aby skopiować"} onClick={() => { navigator.clipboard.writeText(selectedIssue.buyer?.login || ''); setCopyToast(selectedIssue.buyer?.login || ''); setTimeout(() => setCopyToast(''), 1500); }} style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'copy' }}>{selectedIssue.buyer?.login}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>{selectedIssue.account_name}</div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={async () => {
              setSelectedIssue(null);
              setIssuesKey(k => k+1);
              await axios.post(`${API}/allegro/issues/${selectedIssue.id}/handle`);
            }} style={{ background: 'rgba(134,239,172,0.3)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 11 }}>
              ✓
            </button>
            {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
              <button onClick={async () => {
                setShowIssueHistoryMobile(h => !h);
                if (!showIssueHistoryMobile) {
                  setIssueHistoryMobileLoading(true);
                  const res = await axios.get(`${API}/allegro/issues/${selectedIssue.id}/history`);
                  setIssueHistoryMobile(res.data || []);
                  setIssueHistoryMobileLoading(false);
                }
              }} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontSize: 11 }}>
                🕐
              </button>
            )}
            <button onClick={fetchIssueOrders} style={{ background: 'rgba(147,197,253,0.3)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontSize: 11 }}>
              🛒
            </button>
            <BuyerReturnsButton buyerLogin={selectedIssue.buyer?.login || ''} variant='mobile' onOpenReturn={onOpenReturn || (() => {})} />
            <BuyerShipmentsButton buyerLogin={selectedIssue.buyer?.login || ''} variant='mobile' onOpenShipment={onOpenShipment || (() => {})} />
          </div>
        </>
      )}
    </div>
  );

  // Stała dolna nawigacja
  const BottomNav = () => (
    <div style={{ display: 'flex', background: '#1d4ed8', padding: '8px 0',
      paddingBottom: 'env(safe-area-inset-bottom)', flexShrink: 0 }}>
      <button onClick={() => { setView('list'); setSelected(null); }}
        style={{ flex: 1, background: view === 'list' || view === 'thread' ? 'rgba(255,255,255,0.2)' : 'none',
          border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11,
          borderRadius: view === 'list' || view === 'thread' ? 8 : 0 }}>
        <div style={{ fontSize: 20 }}>✉️</div>
        <div>Wiadomości</div>
      </button>
      <button onClick={() => { setView('issues'); setIssuesKey(k => k+1); setSelectedIssue(null); }}
        style={{ flex: 1, background: view === 'issues' ? 'rgba(255,255,255,0.2)' : 'none',
          border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11,
          borderRadius: view === 'issues' ? 8 : 0 }}>
        <div style={{ fontSize: 20 }}>⚖️</div>
        <div>Dyskusje</div>
      </button>
      <button onClick={() => { setView('ratings'); setRatingsKey(k => k+1); }}
        style={{ flex: 1, background: view === 'ratings' ? 'rgba(255,255,255,0.2)' : 'none',
          border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11,
          borderRadius: view === 'ratings' ? 8 : 0 }}>
        <div style={{ fontSize: 20, color: negativeRatings > 0 ? '#fca5a5' : '#fbbf24' }}>★</div>
        <div>Oceny</div>
      </button>
      <button onClick={() => { setView('email'); setEmailKey(k => k+1); }}
        style={{ flex: 1, background: view === 'email' ? 'rgba(255,255,255,0.2)' : 'none',
          border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11,
          borderRadius: view === 'email' ? 8 : 0 }}>
        <div style={{ fontSize: 20 }}>📧</div>
        <div>Email</div>
      </button>
      <button onClick={() => { if (hasSettings) { setView('settings'); setSettingsKey(k => k+1); } else { setView('no-access'); } }}
        style={{ flex: 1, background: view === 'settings' || view === 'no-access' ? 'rgba(255,255,255,0.2)' : 'none',
          border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11,
          borderRadius: view === 'settings' || view === 'no-access' ? 8 : 0 }}>
        <div style={{ fontSize: 20 }}>⚙️</div>
        <div>Ustawienia</div>
      </button>
      <button onClick={onLogout}
        style={{ flex: 1, background: 'none', border: 'none', color: '#fff',
          cursor: 'pointer', padding: '6px', fontSize: 11 }}>
        <div style={{ fontSize: 20 }}>⏻</div>
        <div>Wyloguj</div>
      </button>
    </div>
  );

  // Przechwytuj strzałkę wstecz i zamknięcie karty
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      if (view !== 'list') {
        // Jeśli jesteśmy w podwidoku — wróć do listy zamiast pytać
        setView('list');
        setSelected(null);
        return;
      }
      const confirmed = window.confirm('Czy na pewno chcesz wyjść z aplikacji OPENkontakt?');
      if (confirmed) {
        onLogout();
        window.history.back();
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getCurrentModule = (): string => {
    if (view === 'settings') return 'settings';
    if (view === 'email') return 'email';
    if (view === 'ratings') return 'ratings';
    if (view === 'issues') return 'issues';
    if (view === 'thread' || view === 'list') return 'messages';
    return 'messages';
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFirstVisit = !localStorage.getItem('assistant_seen');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', fontFamily: 'system-ui', background: '#f9fafb' }}>
      <LiveAlertBar currentUser={currentUser} onOpenAiOpen={() => onOpenAiOpen && onOpenAiOpen()} onOpenReturns={() => onOpenReturns && onOpenReturns()} />
      <AIAssistant currentModule="mobile" isFirstVisit={!localStorage.getItem('assistant_seen')} />
      {(view === 'list' || view === 'thread') && accountsLoaded && accounts.length === 0 && !allegroBannerClosed && (
        <MMAllegroSetupBanner message="Aby odbierać i odpowiadać na wiadomości od klientów Allegro, podłącz konto Allegro." returnModule="messages" onClose={() => setAllegroBannerClosed(true)} />
      )}
      {showOrders && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setShowOrders(false)}>
          <div style={{ background: '#fff', borderRadius: '12px 12px 0 0', padding: 20, width: '100%', maxHeight: '75vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>🛒 Zamówienia: {selected?.interlocutor.login}</div>
              <button onClick={() => setShowOrders(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            {loadingOrders && <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Ładowanie...</div>}
            {!loadingOrders && buyerOrders.length === 0 && (
              <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Brak zamówień</div>
            )}
            {!loadingOrders && buyerOrders.map((o, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                {o.image && <img src={o.image} alt={o.offer_name} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                {!o.image && <div style={{ width: 50, height: 50, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{o.offer_name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {o.sku && <span>SKU: {o.sku} · </span>}
                    <span>Ilość: {o.quantity}</span>
                    {o.bought_at && <span> · {new Date(o.bought_at).toLocaleDateString('pl-PL')}</span>}
{(o as any).smart ? <span> · <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>Smart</span></span> : <span style={{ color: '#9ca3af' }}> · bez Smart</span>}
{(o as any).apilo_id && <span onClick={() => { try { navigator.clipboard.writeText(String((o as any).apilo_id)); } catch (e) {} }}> · Apilo: <b style={{ color: '#6d28d9' }}>{(o as any).apilo_id}</b></span>}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                  {o.price} {o.currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <TopBar />

      {/* SEKCJA RATINGS */}
      {view === 'ratings' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Ratings key={ratingsKey} onBack={() => setView('list')} hideHeader={true} expiredAccounts={expiredAccounts} />
        </div>
      )}

      {/* SEKCJA ISSUES */}
      {view === 'issues' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showIssueHistoryMobile && selectedIssue && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
            <div style={{ padding: '6px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, flexShrink: 0 }}>
              {issueHistoryMobileLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
              {!issueHistoryMobileLoading && issueHistoryMobile.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
              {issueHistoryMobile.map((h, i) => (
                <div key={h.id} style={{ display: 'flex', gap: 6, paddingBottom: 3, marginBottom: 3, borderBottom: i < issueHistoryMobile.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                  <span>{h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                  <div>
                    <span style={{ fontWeight: 500, color: '#374151' }}>
                      {h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `→ ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Odpowiedź' : h.event}
                    </span>
                    <span style={{ color: '#9ca3af', marginLeft: 4 }}>{h.actor_name}</span>
                    <div style={{ color: '#9ca3af', fontSize: 10 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Issues key={issuesKey} onBack={() => setView('list')} hideHeader={true} onIssueSelect={(issue: any) => setSelectedIssue(issue)} expiredAccounts={expiredAccounts} currentUser={currentUser} onOpenReturn={onOpenReturn} onOpenShipment={onOpenShipment} />
        </div>
      )}

      {/* SEKCJA WIADOMOŚCI - WĄTEK */}
      {view === 'thread' && selected && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showMsgHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
            <div style={{ padding: '6px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>
              {msgHistoryLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
              {!msgHistoryLoading && msgHistory.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
              {msgHistory.map((h, i) => (
                <div key={h.id} style={{ display: 'flex', gap: 6, paddingBottom: 3, marginBottom: 3, borderBottom: i < msgHistory.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                  <span>{h.event === 'read' ? '👁' : h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                  <div>
                    <span style={{ fontWeight: 500, color: '#374151' }}>
                      {h.event === 'read' ? 'Przeczytano' : h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `→ ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Odpowiedź' : h.event}
                    </span>
                    <span style={{ color: '#9ca3af', marginLeft: 4 }}>{h.actor_name}</span>
                    <div style={{ color: '#9ca3af', fontSize: 10 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '12px', background: '#f9fafb' }}>
            {loadingMsgs && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Ładowanie...</div>}
            {offerInfo && (
              <div style={{ marginBottom: 10, padding: '10px 12px', background: '#fff',
                border: '1px solid #e5e7eb', borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                {offerInfo.image && (
                  <img src={offerInfo.image} alt={offerInfo.name}
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 1 }}>Pytanie dotyczy oferty:</div>
                  <a href={offerInfo.url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, fontWeight: 500, color: '#1d4ed8', textDecoration: 'none',
                      display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {offerInfo.name}
                  </a>
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>#{offerInfo.id}</div>
                </div>
              </div>
            )}
            {messages.map(m => {
              if ((m as any).allegro_system) return <AllegroSystemMessage key={m.id} m={m} />;
              const isClient = m.author?.login === selected.interlocutor.login;
              return (
                <div key={m.id} style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: isClient ? 'flex-start' : 'flex-end' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>
                    {m.author?.login} · {formatDate(m.createdAt)}
                  </div>
                  <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                    background: isClient ? '#fff' : '#1d4ed8',
                    color: isClient ? '#374151' : '#fff',
                    border: isClient ? '1px solid #e5e7eb' : 'none' }}>
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{decodeHtml(m.text || '')}</span>
                    {isClient && threadLang !== 'pl' && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f3f4f6' }}>
                        {showTranslation[m.id] ? (
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>🇵🇱 Tłumaczenie:</div>
                            <div style={{ fontSize: 12 }}>{translations[m.id] || '⏳ Tłumaczę...'}</div>
                            <button onClick={() => setShowTranslation(prev => ({...prev, [m.id]: false}))}
                              style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0' }}>
                              Ukryj
                            </button>
                          </div>
                        ) : (
                          <button onClick={async () => {
                            setShowTranslation(prev => ({...prev, [m.id]: true}));
                            if (!translations[m.id]) {
                              const res = await axios.post(`${API}/ai/translate-to-polish`, { text: decodeHtml(m.text||''), source_lang: threadLang });
                              setTranslations(prev => ({...prev, [m.id]: res.data.translation}));
                            }
                          }} style={{ fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0' }}>
                            🇵🇱 Pokaż tłumaczenie
                          </button>
                        )}
                        </div>
                      )}
                      {!isClient && threadLang !== 'pl' && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                        {showTranslation[m.id] ? (
                          <div>
                            <div style={{ fontSize: 11, color: '#bfdbfe', marginBottom: 3 }}>🇵🇱 Wersja polska:</div>
                            <div style={{ fontSize: 12, color: '#fff' }}>{translations[m.id] || '⏳ Tłumaczę...'}</div>
                            <button onClick={() => setShowTranslation(prev => ({...prev, [m.id]: false}))}
                              style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0' }}>
                              Ukryj
                            </button>
                          </div>
                        ) : (
                          <button onClick={async () => {
                            setShowTranslation(prev => ({...prev, [m.id]: true}));
                            if (!translations[m.id]) {
                              const res = await axios.post(`${API}/ai/translate-to-polish`, { text: decodeHtml(m.text||''), source_lang: threadLang });
                              setTranslations(prev => ({...prev, [m.id]: res.data.translation}));
                            }
                          }} style={{ fontSize: 12, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0' }}>
                            🇵🇱 Pokaż po polsku
                          </button>
                        )}
                      </div>
                    )}
                    {m.attachments && m.attachments.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        {m.attachments.map((att: any, idx: number) => (
                          <div key={idx} style={{ marginTop: 4 }}>
                            {att.mimeType && att.mimeType.startsWith('image/') ? (
                              <a href={`/api${att.proxy_url}&token=${getAuthToken()}`} target="_blank" rel="noreferrer">
                                <img src={`/api${att.proxy_url}&token=${getAuthToken()}`} alt={att.fileName}
                                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block' }} />
                              </a>
                            ) : (
                              <a href={`/api${att.proxy_url}&token=${getAuthToken()}`} target="_blank" rel="noreferrer"
                                style={{ display: 'block', fontSize: 12, color: isClient ? '#1d4ed8' : '#fff' }}>
                                📎 {att.fileName || 'Załącznik'}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid #e5e7eb' }}>
            {sent && <div style={{ fontSize: 12, color: '#15803d', marginBottom: 6 }}>{sentLearned ? '✓ Wysłano i zapisano do bazy AI!' : '✓ Wysłano'}</div>}
            <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>🌐 Klient otrzyma odpowiedź w języku:</span>
              <select value={threadLang} onChange={e => { setThreadLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map((o: any) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            {replyTranslation && (
              <div style={{ fontSize: 12, color: '#3C3489', marginBottom: 6, padding: '6px 8px', background: '#EEEDFE', borderRadius: 6, lineHeight: 1.5 }}>
                <div style={{ fontSize: 11, marginBottom: 3 }}>Podgląd {threadLang.toUpperCase()}:</div>
                {replyTranslation}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <DictateButton onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, (v: any) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); }); }} />
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <textarea ref={replyTextareaRef} value={reply} onChange={e => { handleReplyChange(e.target.value, (v) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); }); }} onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
              placeholder="Wpisz odpowiedź po polsku..."
              rows={3}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8,
                fontSize: 13, fontFamily: 'system-ui', resize: 'none', outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
            {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, (v) => { setReply(v); setReplyTranslation(''); setReplyIsManual(true); })} onClose={() => setShowReplySuggestions(false)} />}
            </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ padding: '10px 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}>
                📎
                <input type="file" style={{ display: 'none' }}
                  accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                  onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
                  disabled={uploadingFile} />
              </label>
              {attachments.map((a, idx) => (
                <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
                  fontSize: 11, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  📄 {a.filename}
                  <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                </span>
              ))}
              {selected && isAccountExpired(selected.account_name || '') && (
              <div style={{ padding: '6px 10px', background: '#fef2f2', borderRadius: 6, fontSize: 11,
                color: '#dc2626', marginBottom: 6, border: '1px solid #fecaca' }}>
                ⚠️ Brak połączenia z {selected.account_name}
              </div>
            )}
            {showOfferPicker && (
                <OfferPicker
                    defaultAccountId={selected?.account_id}
                    onClose={() => setShowOfferPicker(false)}
                    onInsertLink={(link) => {
                      const ta = replyTextareaRef.current;
                      if (ta) {
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        const newVal = reply.slice(0, start) + link + reply.slice(end);
                        setReply(newVal);
                        setReplyIsManual(true);
                        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + link.length, start + link.length); }, 0);
                      } else {
                        setReply(prev => prev + link);
                      }
                      setShowOfferPicker(false);
                    }}
                />
              )}
            <button onClick={() => setShowOfferPicker(v => !v)}
                style={{ padding: '10px 12px', background: showOfferPicker ? '#1d4ed8' : '#f0f9ff',
                  color: showOfferPicker ? '#fff' : '#0369a1', border: '1px solid #bae6fd',
                  borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                🏷️
              </button>
            <button onClick={() => sendReply(false)} disabled={!reply.trim() || isAccountExpired(selected?.account_name || '')}
                style={{ flex: 1, padding: '10px', background: reply.trim() ? '#1d4ed8' : '#9ca3af',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                Wyślij
              </button>
              <button onClick={() => sendReply(true)} disabled={!reply.trim() || isAccountExpired(selected?.account_name || '')}
                style={{ flex: 1, padding: '10px', background: reply.trim() ? '#f0fdf4' : '#f9fafb',
                  color: reply.trim() ? '#15803d' : '#9ca3af', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                Wyślij i ucz
              </button>
              <button onClick={generateAI} disabled={aiLoading}
                style={{ padding: '10px 12px', background: '#f0fdf4', color: '#15803d',
                  border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                {aiLoading ? '⏳' : '✦ AI'}
              </button>
              {threadLang !== 'pl' && reply.trim() && (
                <button onClick={async () => {
                  setTranslatingReply(true);
                  const res = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: threadLang });
                  setReplyTranslation(res.data.translation);
                  setTranslatingReply(false);
                }} disabled={translatingReply}
                  style={{ padding: '10px 12px', background: '#EEEDFE', color: '#3C3489',
                    border: '1px solid #C4C2F5', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                  {translatingReply ? '⏳' : `🌐 ${threadLang.toUpperCase()}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEKCJA EMAIL */}
      {view === 'email' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <EmailInbox key={emailKey} onBack={() => setView('list')} hideHeader={true} hideAlertBar={true} mobile={true} currentUser={currentUser} />
        </div>
      )}
      {/* SEKCJA USTAWIENIA */}
      {view === 'settings' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Settings key={settingsKey} onBack={() => setView('list')} hideHeader={true} currentUser={currentUser} />
        </div>
      )}

      {/* BRAK DOSTEPU */}
      {view === 'no-access' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Brak dostępu</div>
          <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
            Nie masz uprawnień do modułu Ustawienia.<br />Skontaktuj się z administratorem.
          </div>
        </div>
      )}

      {/* SEKCJA WIADOMOŚCI - LISTA */}
      {(view === 'list') && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={(e: any) => { if (e.key === 'Enter') fetchThreads(0, undefined, e.target.value); }}
              placeholder="Szukaj po loginie..."
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
                fontSize: 13, boxSizing: 'border-box', outline: 'none', background: '#fff' }} />
            <button onClick={() => fetchThreads(0, undefined, search)}
              style={{ padding: '8px 14px', background: '#1d4ed8', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Szukaj
            </button>
            {search && <button onClick={() => { setSearch(''); fetchThreads(0, undefined, ''); }}
              style={{ padding: '8px 10px', background: '#f3f4f6', color: '#6b7280', border: 'none',
                borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>✕</button>}
          </div>
          <div style={{ display: 'flex', gap: 6, padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
            {[{k:'unread',l:'Nowe'},{k:'all',l:'Wszystkie'}].map(f => (
              <button key={f.k} onClick={() => { setFilter(f.k); fetchThreads(0, f.k); setSelectedMobileIds(new Set()); }}
                style={{ fontSize: 12, padding: '5px 12px', borderRadius: 20, border: '1px solid #d1d5db',
                  background: filter === f.k ? '#1d4ed8' : 'transparent',
                  color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
                {f.l}
              </button>
            ))}
            <AssignButton
              selectedIds={Array.from(selectedMobileIds)}
              endpoint="/api/assign/messages"
              currentUser={currentUser}
              onAssigned={() => { setSelectedMobileIds(new Set()); fetchThreads(mobilePage); }}
              size="small"
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', background: '#f9fafb' }}>
            {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Ładowanie...</div>}
            {filtered.map(t => (
              <div key={t.id}
                style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                  background: selectedMobileIds.has(t.id) ? '#fef9c3' : selected?.id === t.id ? '#eff6ff' : !t.read ? '#fff' : '#f9fafb',
                  borderLeft: selected?.id === t.id ? '3px solid #1d4ed8' : !t.read ? '3px solid #93c5fd' : '3px solid transparent',
                  display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={selectedMobileIds.has(t.id)}
                  onChange={e => { e.stopPropagation(); const next = new Set(selectedMobileIds); if (e.target.checked) next.add(t.id); else next.delete(t.id); setSelectedMobileIds(next); }}
                  onClick={e => e.stopPropagation()}
                  style={{ marginTop: 4, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectThread(t)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontWeight: !t.read ? 600 : 400, fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                    {t.has_attachments && <span>📎</span>}
                    <PpiBadge t={t} />
                    {t.language && t.language !== 'pl' && LANG_FLAGS[t.language] && <span>{LANG_FLAGS[t.language]}</span>}
                    {t.interlocutor.login}
                  </span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>{formatDate(t.lastMessageDateTime)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5,
                      background: !t.read ? '#1d4ed8' : 'transparent', border: '1px solid #d1d5db' }}></span>
                    {!t.read ? 'Nowa' : 'Przeczytana'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === t.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af' }} /> : null; })()}
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{t.account_name}</span>
                  </div>
                </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && !loading && (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 14 }}>Brak wiadomości</div>
            )}
          </div>
          <div style={{ display: 'flex', background: '#fff', padding: '6px 12px', borderTop: '1px solid #e5e7eb', alignItems: 'center', gap: 6 }}>
            <button onClick={() => fetchThreads(Math.max(0, mobilePage-1))} disabled={mobilePage === 0}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                background: 'transparent', cursor: mobilePage === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
            <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>
              {mobilePage+1}/{Math.max(1, Math.ceil(totalThreadsMobile/MOBILE_PAGE))} · {totalThreadsMobile} wątków
            </span>
            <button onClick={() => fetchThreads(mobilePage+1)} disabled={(mobilePage+1)*MOBILE_PAGE >= totalThreadsMobile}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                background: 'transparent', cursor: (mobilePage+1)*MOBILE_PAGE >= totalThreadsMobile ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
          </div>
        </div>
      )}

      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {"✓ Skopiowano: "}{copyToast}
        </div>
      )}
      <BottomNav />
    </div>
  );
}
